/**
 * @file     ListingStickyBar.tsx
 * @purpose  The bars that take over once the hero's rail actions leave the
 *           viewport (IntersectionObserver, never a scroll distance).
 *           From 768: a 64px chrome-glass top bar under the header with the
 *           listing, the section anchors, the first secondary and the primary.
 *           Below 768: a bottom bar with the price, the principal and interest
 *           line and the primary. It owns the bottom edge (`data-bottom-edge`,
 *           read by BottomSlot) and steps aside while the footer is in view;
 *           the page reserves its height so nothing hides under it.
 *           Both read the actions from ListingActionsProvider.
 */
'use client';

import '@/core/i18n/deferred/rentalsListing';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { useTranslation } from 'react-i18next';
import { LISTING_TABLET_MIN_PX } from '@/core/config/listingActions';
import { listingDetailContent as L } from '@/core/content/listingDetailSharedContent';
import { propertyCanonicalRoute } from '@/core/constants/routes';
import { hubColors } from '@/components/hub/hubKitStyles';
import { SectionAnchors, type SectionAnchorItem } from '@/components/hub/SectionAnchors';
import { BOTTOM_EDGE_ATTR } from '@/core/overlays/BottomSlot';
import { glassMaterial } from '@/design-system/tokens/glass';
import { motionTokens } from '@/design-system/tokens/motion';
import { fontWeightTokens } from '@/design-system/tokens/typography';
import { zLayers } from '@/design-system/tokens';
import { usePropertyNavContext } from '@/hooks/usePropertyNavContext';
import { mlsCardImageLoader } from '@/services/mls/mlsImageLoader';
import { useStickyBarVisibility } from '../hooks/useStickyBarVisibility';
import { ListingActionButton } from './ListingActions';
import { useListingActions } from './ListingActionsContext';
import { usePrincipalAndInterest } from './MonthlyEstimateButton';
import type { ListingStickyBarListing } from './stickyBarListing';

/** The phone bar's height with the safe area; the page reserves the same. */
export const PHONE_BAR_HEIGHT = 'max(106px, calc(73px + env(safe-area-inset-bottom, 0px)))';
const TOP_BAR_HEIGHT = 64;
const PHONE = `@media (max-width: ${LISTING_TABLET_MIN_PX - 0.05}px)`;
const TABLET_UP = `@media (min-width: ${LISTING_TABLET_MIN_PX}px)`;

const cad = (price: number | null): string =>
  price == null
    ? ''
    : new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 }).format(price);

const enter = `${motionTokens.durations.base}ms ${motionTokens.easing.decelerate}`;
const exit = `${motionTokens.durations.fast}ms ${motionTokens.easing.standard}`;

function motionSx(visible: boolean, hiddenTransform: string) {
  const timing = visible ? enter : exit;
  return {
    transform: visible ? 'translateY(0)' : hiddenTransform,
    opacity: visible ? 1 : 0,
    pointerEvents: visible ? 'auto' : 'none',
    transition: `transform ${timing}, opacity ${timing}`,
    '@media (prefers-reduced-motion: reduce)': {
      transform: 'none',
      transition: `opacity ${motionTokens.durations.instant}ms linear`,
    },
  } as const;
}

const Chevron = ({ dir }: { dir: 'left' | 'right' }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <polyline points={dir === 'left' ? '15 18 9 12 15 6' : '9 18 15 12 9 6'} />
  </svg>
);

interface Props {
  listing: ListingStickyBarListing;
  /** Id of the hero's rail actions. */
  anchorId: string;
  /** In-page section anchors, shown in the top bar from 1100px. */
  anchors?: { items: SectionAnchorItem[]; label: string };
}

export function ListingStickyBar({ listing, anchorId, anchors }: Props) {
  const { t } = useTranslation();
  const router = useRouter();
  const { visible, footerInView } = useStickyBarVisibility(anchorId);
  const { actions } = useListingActions();
  const nav = usePropertyNavContext(listing.slug);
  const rental = listing.pricePeriod === 'month';
  const pi = usePrincipalAndInterest(rental ? null : listing.price);
  const price = rental && listing.price != null
    ? `${cad(listing.price)}${t('rentalsListing.price.perMonthShort', { defaultValue: '/mo' })}`
    : cad(listing.price);
  const phoneVisible = visible && !footerInView;
  const a11yLabel = t(L.stickyBar.a11yLabel.key, { defaultValue: L.stickyBar.a11yLabel.defaultValue });
  const secondary = actions.secondary[0];
  const showMonthly = pi && actions.primary !== 'see_similar' && actions.primary !== 'get_listing_alerts';

  const navArrow = (dir: 'left' | 'right') => {
    const to = dir === 'left' ? nav.prev : nav.next;
    const label = dir === 'left' ? 'Previous listing' : 'Next listing';
    return (
      <IconButton
        disabled={!to}
        aria-label={label}
        onClick={() => to && router.push(propertyCanonicalRoute(to))}
        sx={{ width: 40, height: 40, color: hubColors.textSecondary, '&.Mui-focusVisible': { outline: `2px solid ${hubColors.interactiveFocusRing}` } }}
      >
        <Chevron dir={dir} />
      </IconButton>
    );
  };

  return (
    <>
      {/* From 768: a zero-height sticky rail under the header. The bar hangs
          from it absolutely, so showing it never shifts the page (CLS 0). */}
      <Box
        sx={{
          display: 'none',
          [TABLET_UP]: { display: 'block' },
          position: 'sticky',
          top: 'var(--chrome-inset, 0px)',
          height: 0,
          zIndex: zLayers.stickyBar,
        }}
      >
        <Box
          role="region"
          aria-label={a11yLabel}
          aria-hidden={!visible}
          inert={!visible}
          data-listing-top-bar
          data-visible={visible ? 'true' : 'false'}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: TOP_BAR_HEIGHT,
            ...glassMaterial('chrome', { edge: 'bottom' }),
            ...motionSx(visible, 'translateY(-100%)'),
          }}
        >
          <Box sx={{ maxWidth: 1200, height: '100%', mx: 'auto', px: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
            {nav.hasContext ? (
              <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                {navArrow('left')}
                {nav.position != null && nav.total != null ? (
                  <Box component="span" sx={{ fontSize: 12, color: hubColors.textMuted, minWidth: 32, textAlign: 'center' }}>
                    {nav.position}/{nav.total}
                  </Box>
                ) : null}
                {navArrow('right')}
              </Box>
            ) : null}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, minWidth: 0, flex: '1 1 auto', maxWidth: anchors && anchors.items.length > 0 ? 260 : 'none' }}>
              {listing.thumbUrl ? (
                <Box sx={{ position: 'relative', width: 40, height: 40, flexShrink: 0, borderRadius: '8px', overflow: 'hidden', backgroundColor: hubColors.surfaceSunken }}>
                  <Image src={listing.thumbUrl} alt="" fill sizes="40px" loader={mlsCardImageLoader} quality={60} style={{ objectFit: 'cover' }} />
                </Box>
              ) : null}
              <Box sx={{ minWidth: 0 }}>
                <Box sx={{ fontSize: 14, fontWeight: fontWeightTokens.medium, color: hubColors.textPrimary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {listing.title}
                </Box>
                {price ? (
                  <Box sx={{ fontSize: 13, color: hubColors.textSecondary, fontVariantNumeric: 'tabular-nums' }}>{price}</Box>
                ) : null}
              </Box>
            </Box>
            {anchors && anchors.items.length > 0 ? (
              <Box
                data-sticky-anchors
                sx={{
                  display: 'none',
                  '@media (min-width: 1100px)': { display: 'block' },
                  flex: '0 1 auto',
                  minWidth: 0,
                  alignSelf: 'stretch',
                  '& nav, & ul': { height: '100%' },
                  '& ul': { borderBottom: 'none', gap: 0 },
                  '& a': { px: 2, fontSize: 14, minHeight: TOP_BAR_HEIGHT },
                }}
              >
                <SectionAnchors items={anchors.items} ariaLabel={anchors.label} firstCurrentAtTop={false} />
              </Box>
            ) : null}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0, ml: 'auto' }}>
              {secondary ? <ListingActionButton id={secondary} variant="secondary" position="top_bar" height={40} /> : null}
              <ListingActionButton id={actions.primary} variant="primary" position="top_bar" height={40} />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Below 768: the bottom bar. Mobile has no transformed scroll root, so
          fixed positioning is relative to the viewport. */}
      <Box
        role="region"
        aria-label={a11yLabel}
        aria-hidden={!phoneVisible}
        inert={!phoneVisible}
        data-listing-phone-bar
        data-visible={phoneVisible ? 'true' : 'false'}
        {...{ [BOTTOM_EDGE_ATTR]: '' }}
        sx={{
          display: 'none',
          [PHONE]: { display: 'block' },
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          minHeight: PHONE_BAR_HEIGHT,
          px: 5,
          pt: 3,
          pb: 'calc(12px + env(safe-area-inset-bottom, 0px))',
          zIndex: zLayers.stickyBar,
          ...glassMaterial('chrome', { edge: 'top' }),
          ...motionSx(phoneVisible, 'translateY(110%)'),
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, minHeight: 48 }}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            {price ? (
              <Box sx={{ fontSize: 17, fontWeight: fontWeightTokens.semibold, lineHeight: 1.25, color: hubColors.textPrimary, fontVariantNumeric: 'tabular-nums' }}>
                {price}
              </Box>
            ) : (
              <Box sx={{ fontSize: 14, fontWeight: fontWeightTokens.medium, color: hubColors.textPrimary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {listing.title}
              </Box>
            )}
            {rental && listing.rentalLine ? (
              <Box sx={{ fontSize: 12.5, lineHeight: 1.35, color: hubColors.textMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {listing.rentalLine}
              </Box>
            ) : null}
            {showMonthly ? (
              <Box sx={{ fontSize: 12.5, lineHeight: 1.35, color: hubColors.textMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {t(L.summary.monthlyShort.key, { defaultValue: L.summary.monthlyShort.defaultValue, amount: pi.amount, rate: pi.rate })}
              </Box>
            ) : null}
          </Box>
          <Box sx={{ flexShrink: 0 }}>
            <ListingActionButton id={actions.primary} variant="primary" position="phone_bar" />
          </Box>
        </Box>
      </Box>
    </>
  );
}
